import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import { Admin } from "../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  LogisticsAnalyticsFilters,
  LogisticsAnalyticsSelectedFilters,
  parseQueryParamsForLogisticsAnalyticsFilters,
} from "../../../models/Dashboard/LogisticsDashboard/LogisticsAnalyticsFilters";
import { InventoryTypeOptions } from "../../../models/inventory/InventoryListing";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import { formatAmount } from "../../components/common/functions/formatDate";
import { Customer } from "../../../models/Customer/Customer";

const LOGISTICS_ANALYTICS_FILTERS_KEY = "LOGISTICS_ANALYTICS_FILTERS_KEY";

const LogisticsAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    logisticsAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
    customerStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: LogisticsAnalyticsFilters =
    parseQueryParamsForLogisticsAnalyticsFilters(
      localStorage.getItem(LOGISTICS_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(LOGISTICS_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] =
    useState<LogisticsAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<LogisticsAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      LOGISTICS_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.pm_adminids) {
      filters.pm_adminids.forEach((pm_adminids) =>
        newFilters.push({ id: "pm_adminids", value: pm_adminids.value })
      );
    }

    if (filters.inventory_type) {
      newFilters.push({
        id: "inventory_type",
        value: filters.inventory_type.value,
      });
    }

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value })
      );
    }
    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ id: "order_type", value: order_type.value })
      );
    }

    if (filters.customer_id) {
      filters.customer_id.forEach((customer_id) =>
        newFilters.push({ id: "customer_id", value: customer_id.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await logisticsAnalyticsDashboardStore.getLogisticsAnalyticsTats({
        pm_adminids: selectedFilters.pm_adminids,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        customer_id: selectedFilters.customer_id,
        inventory_type: selectedFilters.inventory_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });

      //   await opsAnalyticsDashboardStore.getOrderListTableData({
      //     pm_adminids: selectedFilters.pm_adminids,
      //     order_id: selectedFilters.order_id,
      //     order_type: selectedFilters.order_type,
      //     start_date: selectedFilters.start_date,
      //     end_date: selectedFilters.end_date,
      //     limit: 30,
      //     search: "",
      //     page: 1,
      //     sortBy: "created_date",
      //     sortType: SortType.DESC,
      //   });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "pm_adminids":
        setFilters((prev) => ({
          ...prev,
          pm_adminids:
            filters.pm_adminids?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "customer_id":
        setFilters((prev) => ({
          ...prev,
          customer_id:
            prev.customer_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_adminids: [],
      order_id: [],
      order_type: [],
      customer_id: [],
      inventory_type: InventoryTypeOptions.find((item) => item.id === "2") ?? {
        id: "-1",
        value: "Select",
      },
    }));

    setSelectedFiltersData([]);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText.length > 2) {
      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", err?.response?.data?.message);
        });
      }, 1500);
    }
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_LOGISTICS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 1,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Inventory Type"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={false}
                    value={filters.inventory_type?.id || "2"}
                    list={InventoryTypeOptions.filter(
                      (item) => item.id !== "-1"
                    )}
                    fontSize={"12px"}
                    onSingleSelectionChange={(id, name) => {
                      const item = InventoryTypeOptions.find(
                        (item) => item.id === id
                      );
                      setFilters((prev) => ({
                        ...prev,
                        inventory_type: item,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Customer"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Customer"
                    fontSize="0.9rem"
                    value={filters.customer_id}
                    list={
                      customerStore?.customers?.map((customer: Customer) => ({
                        id: customer?.id?.toString() ?? "",
                        value: customer?.name ?? "",
                      })) || []
                    }
                    local={false}
                    handleSearch={(customer_id) =>
                      handleCustomerSearch(customer_id!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        customer_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Project PMs"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Managers"
                    fontSize="0.9rem"
                    value={filters.pm_adminids}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(pm_adminids) =>
                      handleAdminSearch(pm_adminids!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        pm_adminids: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Order Type"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={true}
                    valuesList={
                      filters.order_type?.map((item) => item.id) || []
                    }
                    list={
                      projectStore?.orderType?.map((order) => ({
                        id: order.id.toString(),
                        value: order.value,
                      })) || []
                    }
                    fontSize={"12px"}
                    onMultiSelectionChange={(ids) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_type:
                          projectStore?.orderType?.filter((item) =>
                            ids.includes(item.id)
                          ) || [],
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id}
                    fontSize="12px"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                <Grid2
                  size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>
            <Box>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {logisticsAnalyticsDashboardStore.basic_tats.map(
                  (item, index) => (
                    <Box
                      key={`${item.label}-${index}`}
                      sx={{
                        flex: {
                          xs: "1 1 140px",
                          sm: "1 1 180px",
                          md: "1 1 200px",
                        },
                        maxWidth: {
                          xs: "calc(50% - 8px)",
                          sm: "calc(33.33% - 16px)",
                          md: "calc(25% - 16px)",
                          lg: "calc(20% - 16px)",
                          xl: "calc(16.66% - 16px)",
                        },
                        minWidth: { xs: "140px", sm: "160px", md: "180px" },
                      }}
                    >
                      <TypeFilterCard
                        title={item.label}
                        message={item.info}
                        value={
                          item.label === "Net Purchase Value"
                            ? formatAmount(item.value)
                            : item.value ?? "0"
                        }
                        color={""}
                        icon={
                          "https://content.swageazy.com/icons/boxes_green.png"
                        }
                        onClick={(title) => {
                          switch (title) {
                            case "Pending Shipments":
                              const pending_shipments_url = `/report/data?title=${encodeURI(
                                "Pending Shipments"
                              )}&base_url=/v5/logistics-analytics&path=/data/pending-shipments&${searchParams.toString()}`;
                              window.open(pending_shipments_url, "_blank");
                              break;
                            case "Shipped Shipments":
                              const shipped_shipments_url = `/report/data?title=${encodeURI(
                                "Shipped Shipments"
                              )}&base_url=/v5/logistics-analytics&path=/data/shipped-shipments&${searchParams.toString()}`;
                              window.open(shipped_shipments_url, "_blank");
                              break;
                            case "Delivered Shipments":
                              const delivered_shipments_url = `/report/data?title=${encodeURI(
                                "Delivered Shipments"
                              )}&base_url=/v5/logistics-analytics&path=/data/delivered-shipments&${searchParams.toString()}`;
                              window.open(delivered_shipments_url, "_blank");
                              break;
                            case "Failed Shipments":
                              const failed_shipments_url = `/report/data?title=${encodeURI(
                                "Failed Shipments"
                              )}&base_url=/v5/logistics-analytics&path=/data/failed-shipments&${searchParams.toString()}`;
                              window.open(failed_shipments_url, "_blank");
                              break;
                            case "Shipped Without AWB":
                              const shipped_without_awb_url = `/report/data?title=${encodeURI(
                                "Shipped Without AWB"
                              )}&base_url=/v5/logistics-analytics&path=/data/shipped-without-awb&${searchParams.toString()}`;
                              window.open(shipped_without_awb_url, "_blank");
                              break;
                            case "Total Shipments":
                            case "Dispatch Tat (Hrs)":
                            case "Delivered Tat (Hrs)":
                              const shipments_url = `/report/data?title=${encodeURI(
                                "Shipments"
                              )}&base_url=/v5/logistics-analytics&path=/data/shipments&${searchParams.toString()}`;
                              window.open(shipments_url, "_blank");
                              break;
                            default:
                              break;
                          }
                        }}
                      />
                    </Box>
                  )
                )}
              </Grid2>
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(LogisticsAnalyticsDashboard);
